export const complexNumbersData = {
  title: "Complex Numbers & Polar Coordinates",
  description: "Master complex numbers in rectangular and polar forms, operations on the complex plane, and De Moivre's Theorem",
  skills: [
    {
      id: "complex-rectangular",
      title: "Complex Numbers in Rectangular Form",
      description: "Perform operations with complex numbers in rectangular form (a + bi), including addition, subtraction, multiplication, division, and finding complex conjugates.",
      examples: [
        {
          problem: "Simplify: (3 + 4i) + (2 - 5i)",
          solution: "Step 1: Add real parts: 3 + 2 = 5\nStep 2: Add imaginary parts: 4i + (-5i) = -i\nStep 3: Combine: 5 - i\n\nAnswer: 5 - i"
        },
        {
          problem: "Multiply: (2 + 3i)(4 - i)",
          solution: "Step 1: Use FOIL method\nFirst: 2 × 4 = 8\nOuter: 2 × (-i) = -2i\nInner: 3i × 4 = 12i\nLast: 3i × (-i) = -3i² = -3(-1) = 3\n\nStep 2: Combine like terms\n8 + (-2i) + 12i + 3 = 11 + 10i\n\nAnswer: 11 + 10i"
        },
        {
          problem: "Divide: (6 + 8i) ÷ (1 + 2i)",
          solution: "Step 1: Multiply by conjugate of denominator\n(6 + 8i)/(1 + 2i) × (1 - 2i)/(1 - 2i)\n\nStep 2: Multiply numerator\n(6 + 8i)(1 - 2i) = 6 - 12i + 8i - 16i²\n= 6 - 4i + 16 = 22 - 4i\n\nStep 3: Multiply denominator\n(1 + 2i)(1 - 2i) = 1 - 4i² = 1 + 4 = 5\n\nStep 4: Simplify\n(22 - 4i)/5 = 22/5 - 4i/5\n\nAnswer: 22/5 - 4i/5 or 4.4 - 0.8i"
        }
      ],
      practice: [
        {
          question: "Simplify: (5 + 2i) + (3 - 7i)",
          options: ["8 - 5i", "8 + 5i", "2 - 5i", "2 + 9i"],
          correctAnswer: 0,
          solution: "Step 1: Add real parts: 5 + 3 = 8\nStep 2: Add imaginary parts: 2i + (-7i) = -5i\nStep 3: Combine: 8 - 5i\n\nAnswer: 8 - 5i"
        },
        {
          question: "Simplify: (4 - 3i) - (2 + 5i)",
          options: ["2 - 8i", "6 - 8i", "2 + 2i", "6 + 2i"],
          correctAnswer: 0,
          solution: "Step 1: Subtract real parts: 4 - 2 = 2\nStep 2: Subtract imaginary parts: -3i - 5i = -8i\nStep 3: Combine: 2 - 8i\n\nAnswer: 2 - 8i"
        },
        {
          question: "Multiply: (3 + 2i)(3 - 2i)",
          options: ["13", "5", "9 - 4i²", "9 + 4"],
          correctAnswer: 0,
          solution: "Step 1: Recognize this is (a + bi)(a - bi) = a² + b²\nStep 2: Calculate: 3² + 2² = 9 + 4 = 13\n\nAlternatively using FOIL:\n9 - 6i + 6i - 4i² = 9 + 4 = 13\n\nAnswer: 13"
        },
        {
          question: "Multiply: (1 + 4i)(2 - 3i)",
          options: ["14 + 5i", "2 - 12i", "14 - 5i", "-10 + 5i"],
          correctAnswer: 0,
          solution: "Step 1: Use FOIL\nFirst: 1 × 2 = 2\nOuter: 1 × (-3i) = -3i\nInner: 4i × 2 = 8i\nLast: 4i × (-3i) = -12i² = 12\n\nStep 2: Combine: 2 - 3i + 8i + 12 = 14 + 5i\n\nAnswer: 14 + 5i"
        },
        {
          question: "Find the complex conjugate of 7 - 3i",
          options: ["7 + 3i", "-7 + 3i", "-7 - 3i", "3 - 7i"],
          correctAnswer: 0,
          solution: "Step 1: The complex conjugate of a + bi is a - bi\nStep 2: For 7 - 3i, change the sign of the imaginary part\nStep 3: Conjugate = 7 + 3i\n\nAnswer: 7 + 3i"
        },
        {
          question: "Divide: (10 + 5i) ÷ (2 + i)",
          options: ["5", "5 - i", "4 + 3i", "5 + i"],
          correctAnswer: 0,
          solution: "Step 1: Multiply by conjugate (2 - i)/(2 - i)\n(10 + 5i)(2 - i) / (2 + i)(2 - i)\n\nStep 2: Numerator: 20 - 10i + 10i - 5i² = 20 + 5 = 25\nStep 3: Denominator: 4 - i² = 4 + 1 = 5\nStep 4: Result: 25/5 = 5\n\nAnswer: 5"
        },
        {
          question: "Simplify: i³⁷",
          options: ["i", "-1", "-i", "1"],
          correctAnswer: 0,
          solution: "Step 1: Recall i¹ = i, i² = -1, i³ = -i, i⁴ = 1\nStep 2: Divide exponent by 4: 37 ÷ 4 = 9 remainder 1\nStep 3: i³⁷ = i¹ = i\n\nAnswer: i"
        },
        {
          question: "Divide: (3 - 4i) ÷ (1 - 2i)",
          options: ["11/5 + 2i/5", "3 + 2i", "-1 + 2i", "11/5 - 2i/5"],
          correctAnswer: 0,
          solution: "Step 1: Multiply by conjugate (1 + 2i)/(1 + 2i)\n(3 - 4i)(1 + 2i) / (1 - 2i)(1 + 2i)\n\nStep 2: Numerator: 3 + 6i - 4i - 8i² = 3 + 2i + 8 = 11 + 2i\nStep 3: Denominator: 1 - 4i² = 1 + 4 = 5\nStep 4: Result: (11 + 2i)/5 = 11/5 + 2i/5\n\nAnswer: 11/5 + 2i/5"
        },
        {
          question: "Find |3 + 4i| (the modulus)",
          options: ["5", "7", "√7", "25"],
          correctAnswer: 0,
          solution: "Step 1: The modulus is |a + bi| = √(a² + b²)\nStep 2: |3 + 4i| = √(3² + 4²)\nStep 3: = √(9 + 16) = √25 = 5\n\nAnswer: 5"
        },
        {
          question: "Simplify: (2i)⁴",
          options: ["16", "-16", "16i", "-16i"],
          correctAnswer: 0,
          solution: "Step 1: (2i)⁴ = 2⁴ × i⁴\nStep 2: = 16 × i⁴\nStep 3: Since i⁴ = 1\nStep 4: = 16 × 1 = 16\n\nAnswer: 16"
        }
      ]
    },
    {
      id: "polar-form",
      title: "Polar Form & Conversions",
      description: "Convert complex numbers between rectangular and polar forms, understand modulus and argument, and perform operations using polar representation.",
      examples: [
        {
          problem: "Convert 1 + i to polar form",
          solution: "Step 1: Find modulus r = √(1² + 1²) = √2\n\nStep 2: Find argument θ\ntan(θ) = 1/1 = 1\nθ = π/4 (45°) [in Quadrant I]\n\nStep 3: Write in polar form\nz = r(cos θ + i sin θ) = √2(cos π/4 + i sin π/4)\nOr: z = √2 cis(π/4)\nOr: z = √2 e^(iπ/4)\n\nAnswer: √2 cis(π/4)"
        },
        {
          problem: "Convert 3 cis(π/3) to rectangular form",
          solution: "Step 1: Use z = r(cos θ + i sin θ)\nz = 3(cos π/3 + i sin π/3)\n\nStep 2: Evaluate trig functions\ncos(π/3) = 1/2\nsin(π/3) = √3/2\n\nStep 3: Multiply\nz = 3(1/2 + i√3/2) = 3/2 + 3i√3/2\n\nAnswer: 3/2 + (3√3/2)i"
        },
        {
          problem: "Multiply in polar form: 2 cis(π/6) × 3 cis(π/4)",
          solution: "Step 1: When multiplying in polar form, multiply moduli and add arguments\nr₁r₂ cis(θ₁ + θ₂)\n\nStep 2: Calculate\n2 × 3 = 6\nπ/6 + π/4 = 2π/12 + 3π/12 = 5π/12\n\nStep 3: Result\n6 cis(5π/12)\n\nAnswer: 6 cis(5π/12)"
        }
      ],
      practice: [
        {
          question: "Find the modulus of -3 + 4i",
          options: ["5", "7", "1", "√7"],
          correctAnswer: 0,
          solution: "Step 1: r = √(a² + b²)\nStep 2: r = √((-3)² + 4²)\nStep 3: r = √(9 + 16) = √25 = 5\n\nAnswer: 5"
        },
        {
          question: "Find the argument of 1 + √3i (in radians)",
          options: ["π/3", "π/6", "π/4", "2π/3"],
          correctAnswer: 0,
          solution: "Step 1: tan(θ) = b/a = √3/1 = √3\nStep 2: Since both a and b are positive, we're in Quadrant I\nStep 3: θ = arctan(√3) = π/3 (60°)\n\nAnswer: π/3"
        },
        {
          question: "Convert -1 - i to polar form",
          options: ["√2 cis(5π/4)", "√2 cis(π/4)", "2 cis(5π/4)", "√2 cis(3π/4)"],
          correctAnswer: 0,
          solution: "Step 1: Find r = √((-1)² + (-1)²) = √2\nStep 2: Find θ: tan(θ) = -1/-1 = 1\nSince both a and b are negative, Quadrant III\nθ = π + π/4 = 5π/4\n\nStep 3: z = √2 cis(5π/4)\n\nAnswer: √2 cis(5π/4)"
        },
        {
          question: "Convert 4 cis(π/2) to rectangular form",
          options: ["4i", "4", "-4i", "4 + 4i"],
          correctAnswer: 0,
          solution: "Step 1: z = 4(cos π/2 + i sin π/2)\nStep 2: cos(π/2) = 0, sin(π/2) = 1\nStep 3: z = 4(0 + i × 1) = 4i\n\nAnswer: 4i"
        },
        {
          question: "Multiply: 2 cis(π/3) × 3 cis(π/6)",
          options: ["6 cis(π/2)", "5 cis(π/2)", "6 cis(π/9)", "6 cis(2π/9)"],
          correctAnswer: 0,
          solution: "Step 1: Multiply moduli: 2 × 3 = 6\nStep 2: Add arguments: π/3 + π/6 = 2π/6 + π/6 = 3π/6 = π/2\nStep 3: Result: 6 cis(π/2)\n\nAnswer: 6 cis(π/2)"
        },
        {
          question: "Divide: 8 cis(5π/6) ÷ 2 cis(π/3)",
          options: ["4 cis(π/2)", "4 cis(π/6)", "6 cis(π/2)", "4 cis(7π/6)"],
          correctAnswer: 0,
          solution: "Step 1: Divide moduli: 8 ÷ 2 = 4\nStep 2: Subtract arguments: 5π/6 - π/3 = 5π/6 - 2π/6 = 3π/6 = π/2\nStep 3: Result: 4 cis(π/2)\n\nAnswer: 4 cis(π/2)"
        },
        {
          question: "Find the argument of -√3 + i (in radians)",
          options: ["5π/6", "π/6", "7π/6", "11π/6"],
          correctAnswer: 0,
          solution: "Step 1: tan(θ) = 1/(-√3) = -1/√3\nStep 2: Reference angle: arctan(1/√3) = π/6\nStep 3: Since a < 0 and b > 0, Quadrant II\nθ = π - π/6 = 5π/6\n\nAnswer: 5π/6"
        },
        {
          question: "Convert 2 cis(3π/4) to rectangular form",
          options: ["-√2 + √2i", "√2 + √2i", "-√2 - √2i", "2 + 2i"],
          correctAnswer: 0,
          solution: "Step 1: z = 2(cos 3π/4 + i sin 3π/4)\nStep 2: cos(3π/4) = -√2/2, sin(3π/4) = √2/2\nStep 3: z = 2(-√2/2 + i√2/2) = -√2 + √2i\n\nAnswer: -√2 + √2i"
        },
        {
          question: "Find the modulus of 5 - 12i",
          options: ["13", "17", "7", "√119"],
          correctAnswer: 0,
          solution: "Step 1: r = √(5² + (-12)²)\nStep 2: r = √(25 + 144) = √169 = 13\n\nAnswer: 13"
        },
        {
          question: "Convert 3 + 3i to polar form",
          options: ["3√2 cis(π/4)", "3 cis(π/4)", "3√2 cis(π/2)", "6 cis(π/4)"],
          correctAnswer: 0,
          solution: "Step 1: r = √(3² + 3²) = √18 = 3√2\nStep 2: tan(θ) = 3/3 = 1, Quadrant I\nθ = π/4\nStep 3: z = 3√2 cis(π/4)\n\nAnswer: 3√2 cis(π/4)"
        }
      ]
    },
    {
      id: "demoivre-theorem",
      title: "De Moivre's Theorem & Complex Roots",
      description: "Apply De Moivre's Theorem to find powers and roots of complex numbers, solve equations in the complex plane, and find nth roots of unity.",
      examples: [
        {
          problem: "Find (1 + i)⁶ using De Moivre's Theorem",
          solution: "Step 1: Convert to polar form\nr = √(1² + 1²) = √2\nθ = π/4\nz = √2 cis(π/4)\n\nStep 2: Apply De Moivre's Theorem: [r cis(θ)]ⁿ = rⁿ cis(nθ)\n(√2)⁶ cis(6 × π/4) = 8 cis(3π/2)\n\nStep 3: Convert back to rectangular\n8(cos 3π/2 + i sin 3π/2) = 8(0 - i) = -8i\n\nAnswer: -8i"
        },
        {
          problem: "Find all cube roots of 8i",
          solution: "Step 1: Convert 8i to polar form\nr = 8, θ = π/2\n8i = 8 cis(π/2)\n\nStep 2: Use nth root formula\nz_k = r^(1/n) cis((θ + 2πk)/n) for k = 0, 1, 2\n\nStep 3: Calculate each root\nk = 0: 2 cis(π/6) = 2(√3/2 + i/2) = √3 + i\nk = 1: 2 cis(5π/6) = 2(-√3/2 + i/2) = -√3 + i\nk = 2: 2 cis(3π/2) = 2(0 - i) = -2i\n\nAnswer: √3 + i, -√3 + i, -2i"
        },
        {
          problem: "Find (2 cis(π/3))⁴",
          solution: "Step 1: Apply De Moivre's Theorem\n[r cis(θ)]ⁿ = rⁿ cis(nθ)\n\nStep 2: Calculate\n2⁴ cis(4 × π/3) = 16 cis(4π/3)\n\nStep 3: Simplify angle if needed\n4π/3 is already in [0, 2π)\n\nAnswer: 16 cis(4π/3) or -8 - 8√3i"
        }
      ],
      practice: [
        {
          question: "Find (2 cis(π/4))³ using De Moivre's Theorem",
          options: ["8 cis(3π/4)", "6 cis(3π/4)", "8 cis(π/12)", "2 cis(3π/4)"],
          correctAnswer: 0,
          solution: "Step 1: Apply [r cis(θ)]ⁿ = rⁿ cis(nθ)\nStep 2: 2³ cis(3 × π/4) = 8 cis(3π/4)\n\nAnswer: 8 cis(3π/4)"
        },
        {
          question: "Find (1 + i)⁴",
          options: ["-4", "4", "-4i", "4i"],
          correctAnswer: 0,
          solution: "Step 1: Convert to polar: √2 cis(π/4)\nStep 2: Apply De Moivre: (√2)⁴ cis(4 × π/4) = 4 cis(π)\nStep 3: Convert back: 4(cos π + i sin π) = 4(-1 + 0i) = -4\n\nAnswer: -4"
        },
        {
          question: "How many distinct 4th roots does any non-zero complex number have?",
          options: ["4", "2", "8", "Infinitely many"],
          correctAnswer: 0,
          solution: "Step 1: The nth roots formula gives n distinct roots\nStep 2: For 4th roots, n = 4\nStep 3: Therefore, there are exactly 4 distinct 4th roots\n\nAnswer: 4"
        },
        {
          question: "Find (√3 + i)⁶",
          options: ["-64", "64", "-64i", "64i"],
          correctAnswer: 0,
          solution: "Step 1: Convert to polar\nr = √(3 + 1) = 2, θ = arctan(1/√3) = π/6\nz = 2 cis(π/6)\n\nStep 2: Apply De Moivre\n2⁶ cis(6 × π/6) = 64 cis(π) = 64(-1) = -64\n\nAnswer: -64"
        },
        {
          question: "Find one square root of i",
          options: ["√2/2 + √2i/2", "i", "1 + i", "√2 + √2i"],
          correctAnswer: 0,
          solution: "Step 1: i = 1 cis(π/2)\nStep 2: Square root: 1^(1/2) cis((π/2 + 2π×0)/2) = cis(π/4)\nStep 3: Convert: cos(π/4) + i sin(π/4) = √2/2 + √2i/2\n\nAnswer: √2/2 + √2i/2"
        },
        {
          question: "Find (3 cis(π/6))²",
          options: ["9 cis(π/3)", "6 cis(π/3)", "9 cis(π/12)", "3 cis(π/3)"],
          correctAnswer: 0,
          solution: "Step 1: Apply De Moivre's Theorem\nStep 2: 3² cis(2 × π/6) = 9 cis(π/3)\n\nAnswer: 9 cis(π/3)"
        },
        {
          question: "The cube roots of unity are evenly spaced around the unit circle. What is the angle between consecutive roots?",
          options: ["2π/3", "π/3", "π/6", "π"],
          correctAnswer: 0,
          solution: "Step 1: n roots are separated by 2π/n radians\nStep 2: For cube roots, n = 3\nStep 3: Angle = 2π/3 radians (120°)\n\nAnswer: 2π/3"
        },
        {
          question: "Find (-1)^(1/3) (one cube root of -1)",
          options: ["cis(π/3)", "cis(π)", "-1", "cis(2π/3)"],
          correctAnswer: 0,
          solution: "Step 1: -1 = 1 cis(π)\nStep 2: Cube root (k=0): 1^(1/3) cis((π + 2π×0)/3) = cis(π/3)\nStep 3: This equals 1/2 + √3i/2\n\nAnswer: cis(π/3)"
        },
        {
          question: "Find (2i)⁵",
          options: ["32i", "-32i", "32", "-32"],
          correctAnswer: 0,
          solution: "Step 1: 2i = 2 cis(π/2)\nStep 2: Apply De Moivre: 2⁵ cis(5π/2)\nStep 3: Simplify angle: 5π/2 = 2π + π/2, so cis(5π/2) = cis(π/2) = i\nStep 4: 32 × i = 32i\n\nAnswer: 32i"
        },
        {
          question: "If z = cis(θ), what is z⁻¹ (the multiplicative inverse)?",
          options: ["cis(-θ)", "cis(θ)", "-cis(θ)", "cis(1/θ)"],
          correctAnswer: 0,
          solution: "Step 1: z⁻¹ = 1/z = 1/[cis(θ)]\nStep 2: Using division in polar form: 1 cis(0) ÷ 1 cis(θ)\nStep 3: = 1 cis(0 - θ) = cis(-θ)\nAlternatively: cis(-θ) is the complex conjugate when |z| = 1\n\nAnswer: cis(-θ)"
        }
      ]
    }
  ]
};